import React from 'react';
import { useAuth } from '@/context/auth-context';
import { Navigate, Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { 
  Gamepad2, 
  ChevronRight, 
  Zap, 
  Crown, 
  Sparkles, 
  Settings, 
  Layers, 
  Activity,
  History,
  Trophy,
  Users
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const Dashboard: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background relative overflow-hidden">
        <div className="absolute inset-0 noise-bg opacity-[0.02] pointer-events-none" />
        <div className="relative z-10 flex flex-col items-center gap-6">
          <div className="w-16 h-16 rounded-3xl bg-secondary border border-zinc-950/5 flex items-center justify-center relative">
            <div className="absolute inset-0 rounded-3xl border-2 border-zinc-950/10 border-t-accent animate-spin" />
            <Activity className="w-6 h-6 text-zinc-300" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-zinc-400 animate-pulse">Initializing Systems</span>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;

  const normalizedPlan = (user?.plan || 'FREE').toUpperCase();

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'ENTERPRISE': return 'bg-cyan-900 text-white border-cyan-800 shadow-cyan-900/10';
      case 'ELITE': return 'bg-[#ffea00] text-black border-amber-400 shadow-amber-500/20';
      case 'PRO': return 'bg-[#d500f9] text-white border-purple-600 shadow-purple-500/20';
      case 'ELITE_STUDIO': return 'bg-[#ffea00] text-black border-amber-400 shadow-amber-500/20';
      case 'CREATOR_PRO': return 'bg-[#d500f9] text-white border-purple-600 shadow-purple-500/20';
      default: return 'bg-[#00e5ff] text-black border-cyan-400 shadow-cyan-500/10';
    }
  };

  const planFeatures = {
    FREE: ['Standard Sandbox', 'Local Saves'],
    PRO: ['Advanced Audio', '4K Rendering', 'Unlimited Cloud Saves'],
    ELITE: ['Custom Watermarks', 'API Access', 'Enterprise Support'],
    ENTERPRISE: ['Whitelabeling', 'Dedicated Engine', 'Custom Branding'],
  };

  return (
    <div className="min-h-screen pb-20 relative overflow-hidden font-sans selection:bg-primary selection:text-primary-foreground bg-[#fcfdff]">
      <SEO title="Dashboard" noindex />
      <div className="absolute inset-x-0 top-0 h-[600px] bg-gradient-to-b from-[#f0f7ff] to-transparent -z-10" />
      {/* Texture Layer */}
      <div className="absolute inset-0 noise-bg opacity-[0.01] pointer-events-none -z-10" />

      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none -z-10">
        <div className="absolute top-0 right-0 w-full h-[600px] bg-[radial-gradient(circle_at_70%_0%,rgba(6,182,212,0.06)_0%,transparent_70%)]" />
        <div className="absolute bottom-0 left-0 w-full h-[600px] bg-[radial-gradient(circle_at_20%_100%,rgba(6,182,212,0.03)_0%,transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-24 relative z-10">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8 mb-12"
        >
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="font-sans text-[9px] tracking-[0.3em] uppercase px-3 py-1 border-cyan-600/10 text-cyan-600 bg-cyan-600/5 backdrop-blur-sm rounded-full">
                Welcome back
              </Badge>
              <div className={cn(
                "px-3 py-1 rounded-full text-[9px] font-bold tracking-[0.2em] uppercase border shadow-sm transition-all duration-300", 
                getPlanBadgeColor(normalizedPlan)
              )}>
                {normalizedPlan === 'FREE' ? 'FREE Member' : normalizedPlan}
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-black font-display text-cyan-950 tracking-tighter uppercase leading-[0.9]">
              Dashboard
            </h1>
          </div>

          <div className="flex flex-wrap gap-4">
            <Link to="/settings">
              <Button variant="outline" size="lg" className="rounded-full border-zinc-950/10 hover:bg-secondary px-8 gap-2 h-14 group transition-all">
                <Settings className="w-4 h-4 text-zinc-400 group-hover:rotate-45 transition-transform duration-500" />
                <span className="font-bold text-xs uppercase tracking-widest">Settings</span>
              </Button>
            </Link>
            <Link to="/plans">
              <Button size="lg" className="rounded-full bg-cyan-600 text-white hover:bg-cyan-700 px-8 gap-2 h-14 shadow-2xl shadow-cyan-600/20 group transition-all">
                <Crown className="w-4 h-4 text-white transition-transform group-hover:scale-110" />
                <span className="font-bold text-xs uppercase tracking-widest">Upgrade</span>
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Action Panels */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Creator Studio - PRIMARY ACTION */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-8 group"
          >
            <Link 
              to="/studio" 
              className="relative block h-full overflow-hidden p-10 md:p-14 rounded-[2rem] border border-cyan-600/10 bg-white/50 backdrop-blur-sm hover:border-cyan-600/30 transition-all duration-700 hover:shadow-[0_40px_100px_-20px_rgba(6,182,212,0.1)] shadow-sm group"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center gap-12 relative z-10">
                <div className="w-24 h-24 rounded-3xl bg-cyan-600 text-white flex items-center justify-center shadow-2xl shadow-cyan-600/20 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700">
                  <Zap className="w-10 h-10 fill-current text-white" />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-5xl md:text-6xl font-bold font-display uppercase tracking-tighter mb-4 text-cyan-950 leading-none">
                    Creator <br /> Studio
                  </h3>
                  <p className="text-lg leading-relaxed font-medium font-sans text-muted-foreground max-w-lg mb-8">
                    Access high-fidelity physics engines, custom audio synthesis, and professional export tools.
                  </p>
                  
                  {/* Plan Insight */}
                  <div className="bg-zinc-50/50 backdrop-blur-sm rounded-2xl p-6 border border-zinc-950/5 flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                      <div className="flex -space-x-3">
                        {[Activity, Layers, Sparkles].map((Icon, idx) => (
                          <div key={idx} className="w-10 h-10 rounded-full bg-white border border-zinc-950/10 flex items-center justify-center shadow-sm">
                            <Icon className="w-4 h-4 text-zinc-400" />
                          </div>
                        ))}
                      </div>
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em] pl-2">
                        {planFeatures[normalizedPlan as keyof typeof planFeatures]?.join(' • ') || 'Standard Features'}
                      </span>
                    </div>
                    <div className="w-full sm:w-auto px-8 py-3 bg-white rounded-full border border-cyan-600/10 shadow-sm flex items-center justify-center gap-2 group-hover:bg-cyan-600 group-hover:text-white transition-all duration-500 font-bold text-[10px] uppercase tracking-widest text-cyan-600">
                      <span>Launch Engine</span>
                      <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-96 h-96 bg-accent/5 blur-[100px] rounded-full group-hover:bg-accent/10 transition-colors duration-700" />
            </Link>
          </motion.div>

          {/* User Profile Summary / Right Aside */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-4 flex flex-col gap-8"
          >
             {/* Plan Details */}
             <div className="p-8 rounded-[2rem] border border-cyan-600/10 bg-cyan-600/5 shadow-sm overflow-hidden relative group">
                <div className="absolute inset-0 noise-bg opacity-[0.05] pointer-events-none" />
                <div className="flex items-center justify-between mb-10 relative z-10 lowercase italic font-display">
                   <h4 className="font-bold uppercase tracking-[0.3em] text-[10px] text-cyan-600/60 not-italic font-sans">Current Plan</h4>
                   <Trophy className="w-5 h-5 text-cyan-600 transition-transform group-hover:scale-125" />
                </div>
                <div className="text-5xl font-bold font-display tracking-tighter mb-4 relative z-10 uppercase text-cyan-600">{normalizedPlan}</div>
                <p className="text-cyan-600/70 text-sm font-medium relative z-10 max-w-[220px] leading-relaxed">
                  {normalizedPlan === 'FREE' ? 'Upgrade to unlock high-fidelity exports and audio racks.' : 'You have full access to our premium creation suite.'}
                </p>
                
                <div className="absolute bottom-0 right-0 -translate-x-1/4 translate-y-1/2 w-48 h-48 bg-cyan-600/10 blur-[60px] rounded-full group-hover:bg-cyan-600/20 transition-all duration-700" />
             </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
